import { captureEvent } from './posthog';

// Event tracking functions based on the Event Tracking Matrix

// Authentication Events
export const trackLoginInitiated = (method: string, referrer?: string, device?: string) => {
  captureEvent('login_initiated', {
    method,
    referrer,
    device
  });
};

export const trackLoginSuccess = (premiumUntil: string | null, timeToLogin?: number) => {
  // Check if plan is active (premiumUntil > today)
  const isPlanActive = premiumUntil ? new Date(premiumUntil) > new Date() : false;
  
  captureEvent('login_success', {
    is_plan_active: isPlanActive,
    time_taken_to_login_in_seconds: timeToLogin
  });
};

export const trackLoginFailed = (method: string, error: string, referrer?: string) => {
  captureEvent('login_failed', {
    method,
    error,
    referrer
  });
};

export const trackLogoutInitiated = () => {
  captureEvent('logout_initiated');
};

// Onboarding Events
export const trackOnboardingPhonePrompt = () => {
  captureEvent('onboarding_phone_prompt');
};

export const trackOnboardingPhoneCompleted = (phoneCountry: string) => {
  captureEvent('onboarding_phone_submitted', {
    phone_country: phoneCountry
  });
};

export const trackOnboardingPhoneSkipped = () => {
  captureEvent('onboarding_phone_skipped');
};

// Navigation Events
export const trackDashboardViewed = () => {
  captureEvent('dashboard_viewed');
};

export const trackProfileViewed = () => {
  captureEvent('profile_viewed');
};

export const trackSettingsViewed = () => {
  captureEvent('settings_opened');
};

export const trackArchivedPostsViewed = () => {
  captureEvent('archived_posts_viewed');
};

export const trackFavouritePostsViewed = () => {
  captureEvent('favourite_posts_viewed');
};

export const trackSubscriptionViewed = () => {
  captureEvent('subscription_viewed');
};

// Engagement Events
export const trackSearchExecuted = (query: string, filters?: any) => {
  captureEvent('search_executed', {
    query,
    filters
  });
};

export const trackSearchFilteredCleared = () => {
  captureEvent('search_filtered_cleared');
};

// Header Tab Events
export const trackHeaderTabClick = (tabName: string) => {
  let eventName = '';
  
  switch (tabName) {
    case 'Features':
      eventName = 'features_tab_click';
      break;
    case 'Pricing':
      eventName = 'pricing_tab_click';
      break;
    case 'Blogs':
      eventName = 'blogs_tab_click';
      break;
    default:
      break;
  }
  
  if (eventName) {
    captureEvent(eventName);
  }
};

// Footer Link Events
export const trackFooterLinkClick = (linkText: string, url: string) => {
  captureEvent('footer_link_click', {
    link_text: linkText,
    url: url
  });
};


export const trackPlatformFilterApplied = (platform: string) => {
  captureEvent('platform_filter_applied', {
    platform
  });
};

export const trackCategoryFilterApplied = (category: string, display_value:string) => {
  captureEvent('category_filter_applied', {
    category,
    display_value
  });
};

export const trackSortOrderChanged = (sortOrder: string) => {
  captureEvent('sort_order_changed', {
    sort_order: sortOrder
  });
};

export const trackPostCardOpened = (postId: string, category: string, platform: string) => {
  captureEvent('post_card_opened', {
    post_id: postId,
    category,
    platform
  });
};

export const trackPostMediaViewed = (postId: string, mediaType: string) => {
  captureEvent('post_media_viewed', {
    post_id: postId,
    media_type: mediaType
  });
};


export const trackPostShared = (postId: string, platform: string) => {
  captureEvent('post_shared', {
    post_id: postId,
    platform
  });
};

// Content Management Events
export const trackPostArchived = (postId: string, platform: string, source: string) => {
  captureEvent('post_archived', {
    post_id: postId,
    platform,
    source
  });
};

export const trackPostUnarchived = (postId: string, platform: string) => {
  captureEvent('post_unarchived', {
    post_id: postId,
    platform
  });
};

export const trackPostFavourited = (postId: string, platform: string) => {
  captureEvent('post_favourited', {
    post_id: postId,
    platform
  });
};

export const trackPostUnfavourited = (postId: string, platform: string) => {
  captureEvent('post_unfavourited', {
    post_id: postId,
    platform
  });
};

export const trackPostDeleted = (postId: string, platform: string) => {
  captureEvent('post_deleted', {
    post_id: postId,
    platform
  });
};

export const trackPostCategorized = (postId: string, categoryId: string, categoryName: string) => {
  captureEvent('post_categorized', {
    post_id: postId,
    category_id: categoryId,
    category_name: categoryName
  });
};

export const trackPostNoteAdded = (postId: string, noteLength: number) => {
  captureEvent('note_saved', {
    post_id: postId,
    note_length: noteLength
  });
};

export const trackNoteEditInitiated = (postId: string) => {
  captureEvent('note_edit_initiated', {
    post_id: postId
  });
};

export const trackNoteDeleted = (postId: string, wordCount: number) => {
  captureEvent('note_deleted', {
    post_id: postId,
    word_count: wordCount
  });
};

// Billing Events
export const trackPlanUpgradeInitiated = (currentPlan: string, targetPlan: string, target_plan_frequency: string) => {
  captureEvent('plan_upgrade_started', {
    current_plan: currentPlan,
    target_plan: targetPlan,
    target_plan_frequency 
  });
};

export const trackPlanUpgradeCompleted = (newPlan: string, paymentMethod: string, amount?: number) => {
  captureEvent('plan_upgrade_completed', {
    new_plan: newPlan,
    payment_method: paymentMethod,
    amount
  });
};

export const trackSubscriptionCancelled = (plan: string, reason?: string) => {
  captureEvent('subscription_cancelled', {
    plan,
    reason
  });
};

export const trackRetainAttempted = (
  status: string,
  salvageAttemptResult: any,
  salvageOfferResult: any,
  additionalFeedback: string | null,
  cancelReason: string | null,
  satisfactionInsight: string | null,
  salvageAttemptIntended: string | null,
  salvageAttemptUsed: string | null
) => {
  captureEvent('retain_attempted', {
    status,
    salvage_attempt_result: salvageAttemptResult,
    salvage_offer_result: salvageOfferResult,
    additional_feedback: additionalFeedback,
    cancel_reason: cancelReason,
    satisfaction_insight: satisfactionInsight,
    salvage_attempt_intended: salvageAttemptIntended,
    salvage_attempt_used: salvageAttemptUsed
  });
};


export const trackDashboardSessionEnded = (sessionDuration: number, sessionId?: string) => {
  captureEvent('dashboard_session_ended', {
    session_duration: sessionDuration,
    session_id: sessionId
  });
};
